/**
 * Adresse et clé PUBLIQUE du projet Supabase d'une console, choisies AU BUILD.
 *
 * Préparé pour le passage à Supabase auto-hébergé (`infra/vps/migration/RUNBOOK.md` § 7) : l'adresse
 * de l'API (`https://<ref>.supabase.co` → `https://api-<produit>.yiriwa-group.com`) et la clé publique
 * (`sb_publishable_…` → clé « anon » JWT) changent sans toucher au code. Chaque console lit ses
 * variables Vite dans SON `src/api/projet.ts` et appelle cette fonction avec ses valeurs par défaut
 * (= la production actuelle) :
 *
 *   VITE_SUPABASE_URL        adresse de l'API, sans barre finale (elle est retirée sinon)
 *   VITE_SUPABASE_ANON_KEY   clé publique (`VITE_SUPABASE_KEY` est acceptée comme ancien nom)
 *
 * Variable absente OU vide → valeur par défaut. Rien ne change tant qu'on ne fournit rien.
 * `scripts/build-vercel.mjs` alimente ces variables console par console, depuis
 * `SWIFTEATS_SUPABASE_*`, `SWIFTDELIVER_SUPABASE_*` et `SWIFTSHOP_SUPABASE_*` : un seul jeu
 * `VITE_SUPABASE_*` pour tout le dépôt enverrait les consoles SwiftDeliver sur la base SwiftEats.
 *
 * ⚠️ Jamais de clé `service_role` / `sb_secret_`, ni ici ni en variable : tout ce que Vite lit dans
 * `import.meta.env.VITE_*` finit EN CLAIR dans le bundle JavaScript.
 *
 * Module pur (aucun `import.meta`) : testable et partagé par les six consoles.
 */
export interface ConfigSupabase {
  url: string
  cle: string
}

/** Ce que la console a lu dans `import.meta.env` (une variable absente vaut `undefined`). */
export interface ValeursDeBuild {
  url?: string | undefined
  cle?: string | undefined
  /** Ancien nom de la clé (`VITE_SUPABASE_KEY`), lu seulement si `cle` est vide. */
  ancienneCle?: string | undefined
}

function renseignee(v: unknown): string {
  return typeof v === 'string' ? v.trim() : ''
}

export function choisirConfigSupabase(build: ValeursDeBuild, parDefaut: ConfigSupabase): ConfigSupabase {
  const url = renseignee(build.url).replace(/\/+$/, '') || parDefaut.url
  const cle = renseignee(build.cle) || renseignee(build.ancienneCle) || parDefaut.cle
  /* Une adresse mal formée (`api-eats.yiriwa-group.com` sans schéma, guillemets collés…) ne doit PAS
     produire une console qui « marche » à moitié : on refuse tout de suite, avec la valeur fautive.
     Une adresse VIDE reste permise — c'est l'état de SwiftShop tant que son projet n'existe pas. */
  if (url !== '' && !/^https?:\/\/[^/\s"']+/.test(url)) {
    throw new Error(`Adresse de l'API invalide : « ${url} » (attendu : https://hôte, sans guillemets).`)
  }
  return { url, cle }
}
